import PhotoProcessingDetails from './PhotoProcessingDetails';
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import TagManager from './TagManager';
import MobilePhotoSwiper from './MobilePhotoSwiper';
import EmblaPhotoViewer from './EmblaPhotoViewer';
import ExifPanel from './ExifPanel';
import InlineExif from './InlineExif';
import { FiHeart, FiX, FiTag, FiCamera, FiInfo, FiTrash2, FiRotateCw, FiMaximize2, FiMinimize2, FiDownload, FiEdit2, FiShare2, FiGrid, FiImage, FiMinus, FiPlus, FiRefreshCw, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { FaHeart } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { cacheStrategy } from '../utils/cacheStrategy';
import { getPrivateCacheScope } from '../utils/privateCacheScope.js';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch, api } from '../utils/api';
import { invalidateImageCache } from '../utils/imagePreloader';
import { photoCache, usePhotoVariants, buildVariantUrl, getPhotoVersion } from '../utils/photoCache';
import useIsMobile from '../utils/useIsMobile';
import { getFeatureFlags } from '../utils/featureFlags';
import {
  isProcessingLike,
  isRecoverableProcessingFailure,
  isTerminalProcessingFailure,
  normalizeProcessingError
} from '../utils/processingState';
import { showToast } from './NotificationTray';
import { useDragSelect } from '../hooks/useLassoSelection';

const formatBytes = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return ', ';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatEtaMs = (ms) => {
  if (!ms || !Number.isFinite(ms) || ms <= 0) return null;
  if (ms < 1000) return '<1 sec';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `~${sec}s`;
  const min = Math.floor(sec / 60);
  const remainingSec = sec % 60;
  if (min < 60) return `~${min}m ${remainingSec}s`;
  const hrs = Math.floor(min / 60);
  const remainingMin = min % 60;
  return `~${hrs}h ${remainingMin}m`;
};

const isProcessing = (img) => {
  return isProcessingLike(img);
};

const isFailed = (img) => {
  return isTerminalProcessingFailure(img);
};

const isRecovering = (img) => {
  return isRecoverableProcessingFailure(img);
};

const getFailureMessage = (img) => {
  const parsed = normalizeProcessingError(img?.processing_error);
  if (parsed?.message) return parsed.message;
  if (parsed?.category) return String(parsed.category).replace(/_/g, ' ');
  return 'Needs attention';
};

const PROCESSING_PLACEHOLDER = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="240" viewBox="0 0 320 240">' +
  '<rect width="320" height="240" fill="#f3f4f6"/>' +
  '<rect x="60" y="50" width="200" height="140" rx="8" fill="#e5e7eb" stroke="#d1d5db" stroke-width="2"/>' +
  '<text x="160" y="125" text-anchor="middle" fill="#6b7f7d" font-family="Arial, sans-serif" font-size="12">processing</text>' +
  '</svg>'
)}`;

const PREVIEW_RETRY_LIMIT = 6;
const PREVIEW_RETRY_BASE_DELAY = 1500;
const PREVIEW_RETRY_MAX_DELAY = 12000;
const HIRES_RETRY_LIMIT = 6;
const HIRES_FALLBACK_RETRY_LIMIT = 3;
const HIRES_RETRY_BASE_DELAY = 2000;
const HIRES_RETRY_MAX_DELAY = 15000;

const appendRetryParam = (url, token) => {
  if (!url || !token) return url;
  if (url.startsWith('blob:') || url.startsWith('data:')) return url;
  // Don't modify signed media URLs - adding params can invalidate signature checks.
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const hasAwsSig = parsed.searchParams.has('X-Amz-Signature') || parsed.searchParams.has('X-Amz-Credential');
    const hasTokenSig = parsed.searchParams.has('token') && parsed.searchParams.has('expires');
    if (hasAwsSig || hasTokenSig) return url;
  } catch {
    if (url.includes('X-Amz-Signature')) return url;
    if (url.includes('token=') && url.includes('expires=')) return url;
  }
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}retry=${token}`;
};

function PhotoTileImage({ img, viewMode, onPreviewRecovered, onCdnFallback, useFastThumbnails = false, onRetry, onDetails }) {
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retryToken, setRetryToken] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const tileImageRef = useRef(null);
  const [hiResReady, setHiResReady] = useState(false);
  const [hiResRetryCount, setHiResRetryCount] = useState(0);
  const [hiResVariant, setHiResVariant] = useState('xl');
  const [hiResFallbackUsed, setHiResFallbackUsed] = useState(false);
  const recoveryRequestedRef = useRef(false);
  const originFallbackRequestedRef = useRef(false);
  const prevBaseUrlRef = useRef('');
  const processing = isProcessing(img);
  const failed = isFailed(img);
  const recovering = isRecovering(img) || img.processing_status === 'recovering';
  const failureMessage = failed ? getFailureMessage(img) : '';
  // Probe only unfinished uploads. Ready tiles must stay thumbnail-sized.
  // Keep a decoded larger preview selected while the visible img decodes it.
  // Otherwise resetting loaded switches back to the thumbnail, which becomes
  // loaded and selects the larger source again in an endless layout-effect loop.
  const canProbeHiRes = processing && (loaded || hiResReady);
  // Use 'sm' for grid thumbnails (320px is plenty for ~125-200px tiles)
  // Use 'md' when fastThumbnails is explicitly enabled
  // xl is only for lightbox/full-screen viewing
  const baseVariant = useFastThumbnails ? 'sm' : 'md';
  const hiResDefaultVariant = 'lg';

  // Only load small variants for thumbnails - xl is loaded on-demand for lightbox
  const variantList = useFastThumbnails
    ? [baseVariant] // sm only for fast mode
    : (canProbeHiRes ? [baseVariant, 'lg'] : [baseVariant]); // md, lg for normal mode
  const variantUrls = usePhotoVariants(img, { variants: variantList, allowNetwork: !processing || loaded, returnObjectUrl: false });

  // Use baseVariant for initial display URL - avoid loading xl for thumbnails
  const baseUrl = variantUrls[baseVariant] || buildVariantUrl(img, baseVariant) || variantUrls.md || buildVariantUrl(img, 'md') || '';
  const lg = canProbeHiRes ? (variantUrls.lg || buildVariantUrl(img, 'lg') || '') : '';
  const xl = canProbeHiRes ? (variantUrls.xl || buildVariantUrl(img, 'xl') || '') : '';
  const hiResUrl = hiResVariant === 'xl'
    ? xl
    : hiResVariant === 'lg'
      ? lg
      : (variantUrls.md || buildVariantUrl(img, 'md') || '');
  const sameAsBase = Boolean(baseUrl && hiResUrl && baseUrl === hiResUrl);
  const baseSrc = baseUrl ? appendRetryParam(baseUrl, retryToken) : PROCESSING_PLACEHOLDER;
  const hiResSrc = hiResReady && !sameAsBase && hiResUrl ? appendRetryParam(hiResUrl, retryToken) : '';
  const displaySrc = hiResSrc || baseSrc;
  const baseWidth = baseVariant === 'sm' ? 320 : baseVariant === 'md' ? 640 : baseVariant === 'lg' ? 1280 : 2560;
  const hiResWidth = hiResVariant === 'lg' ? 1280 : hiResVariant === 'xl' ? 2560 : 640;
  // Don't use srcset for thumbnails - single appropriately-sized image is better
  const srcSet = undefined;

  // Events from a replaced request must not update the current thumbnail.
  // currentSrc can still refer to the old decoded image while src is loading.
  const isCurrentTileSource = (element) => {
    if (!element || element !== tileImageRef.current || !displaySrc) return false;
    const expected = new URL(displaySrc, element.ownerDocument.baseURI).href;
    return element.src === expected && (!element.currentSrc || element.currentSrc === expected);
  };
  const isDecodedTile = (element) => isCurrentTileSource(element)
    && !displaySrc.startsWith('data:image/svg+xml')
    && element.complete && element.naturalWidth > 0;

  // A cached load can finish before passive effects. Read the committed DOM
  // before paint, and never reset a successful onLoad in a later passive effect.
  useLayoutEffect(() => {
    setLoaded(isDecodedTile(tileImageRef.current));
  }, [displaySrc, viewMode]);

  // Reset error/recovery state when the base URL changes, not loaded state.
  useEffect(() => {
    const urlChanged = baseUrl !== prevBaseUrlRef.current;
    prevBaseUrlRef.current = baseUrl;
    if (baseUrl) {
      setLoadError(false);
      setRetryCount(0);
      setRetryToken(0);
    }
    if (urlChanged) {
      setHiResReady(false);
      setHiResRetryCount(0);
      setHiResVariant(hiResDefaultVariant);
      setHiResFallbackUsed(false);
      recoveryRequestedRef.current = false;
      originFallbackRequestedRef.current = false;
    }
  }, [baseUrl, hiResDefaultVariant, img?.id, img?._v, img?._force_origin]);

  useEffect(() => {
    if (!loadError || processing || failed) return;
    if (retryCount >= PREVIEW_RETRY_LIMIT) return;
    const delay = Math.min(
      PREVIEW_RETRY_MAX_DELAY,
      PREVIEW_RETRY_BASE_DELAY * (retryCount + 1)
    );
    const timer = setTimeout(() => {
      setRetryToken(Date.now());
      setRetryCount((count) => count + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [loadError, processing, failed, retryCount]);

  useEffect(() => {
    if (failed || !canProbeHiRes || sameAsBase) return;
    if (!hiResUrl || hiResUrl === baseUrl) return;
    if (hiResReady) return;
    const retryLimit = hiResVariant === 'xl' ? HIRES_FALLBACK_RETRY_LIMIT : HIRES_RETRY_LIMIT;
    if (hiResRetryCount >= retryLimit) return;
    let cancelled = false;
    let timer;
    const probe = new Image();
    probe.onload = () => {
      if (cancelled) return;
      setHiResReady(true);
    };
    probe.onerror = () => {
      if (cancelled) return;
      const delay = Math.min(
        HIRES_RETRY_MAX_DELAY,
        HIRES_RETRY_BASE_DELAY * (hiResRetryCount + 1)
      );
      timer = setTimeout(() => {
        if (!cancelled) {
          setHiResRetryCount((count) => count + 1);
        }
      }, delay);
    };
    probe.src = appendRetryParam(hiResUrl, `${Date.now()}-${hiResRetryCount}`);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [canProbeHiRes, failed, hiResUrl, baseUrl, hiResReady, hiResRetryCount, hiResVariant, sameAsBase]);

  useEffect(() => {
    if (failed || !canProbeHiRes || sameAsBase) return;
    if (hiResReady) return;
    if (hiResFallbackUsed) return;
    const threshold = hiResVariant === 'xl' ? HIRES_FALLBACK_RETRY_LIMIT : HIRES_RETRY_LIMIT;
    if (hiResRetryCount < threshold) return;
    const lgUrl = buildVariantUrl(img, 'lg');
    if (!lgUrl) return;
    setHiResVariant('lg');
    setHiResRetryCount(0);
    setHiResFallbackUsed(true);
  }, [canProbeHiRes, failed, hiResReady, hiResVariant, hiResFallbackUsed, hiResRetryCount, img, sameAsBase]);

  const handleError = (e) => {
    const element = e.currentTarget;
    if (!isCurrentTileSource(element) || !element.complete || element.naturalWidth > 0) return;
    setLoaded(false);
    const src = String(e?.target?.src || '');
    if (!originFallbackRequestedRef.current && img?.cdn_urls && !img?._force_origin && !src.startsWith('data:image/svg+xml')) {
      originFallbackRequestedRef.current = true;
      onCdnFallback?.(img?.id);
      setRetryToken(Date.now());
      setLoadError(false);
      return;
    }
    if (hiResReady && baseUrl) {
      setHiResReady(false);
      setLoadError(false);
      return;
    }
    if (e.target.src !== PROCESSING_PLACEHOLDER) {
      e.target.src = PROCESSING_PLACEHOLDER;
      e.target.srcset = '';
      setLoadError(true);
    }
  };

  const handleLoad = (event) => {
    if (!isDecodedTile(event.currentTarget)) return;
    const src = event?.target?.src || '';
    const isPlaceholder = src.startsWith('data:image/svg+xml');
    if (!isPlaceholder) {
      setLoaded(true);
    }
    if (failed && baseUrl && !recoveryRequestedRef.current && !isPlaceholder) {
      recoveryRequestedRef.current = true;
      onPreviewRecovered?.(img?.id, { forceReady: false });
    }
    if (processing && !isPlaceholder && !recoveryRequestedRef.current) {
      recoveryRequestedRef.current = true;
      onPreviewRecovered?.(img?.id, { forceReady: false });
    }
    if (!processing && !failed && baseUrl && !isPlaceholder) {
      setLoadError(false);
      setRetryCount(0);
      setRetryToken(0);
    }
  };

  const retrying = loadError && !processing && !failed && retryCount < PREVIEW_RETRY_LIMIT;
  const showFailed = failed;
  const showSpinner = (processing && !loaded) || showFailed || loadError || retrying;
  const hasVariantUrl = Boolean(displaySrc && !displaySrc.includes('placeholder') && !displaySrc.startsWith('data:'));
  const statusText = showFailed
    ? 'Preview failed'
    : processing && !hasVariantUrl
        ? (recovering ? 'Recovery pending' : (img.processing_status === 'queued' ? 'Queued for processing' : 'Preparing preview'))
      : processing && hasVariantUrl
          ? 'Loading preview…'
          : retrying
            ? 'Loading preview…'
            : 'Preview unavailable';
  const compactStatusText = statusText === 'Queued for processing' ? 'Queued' : statusText;
  const chipStatusText = recovering || img.processing_status === 'recovering' ? 'Recovery pending' : img.processing_status === 'queued' ? 'Queued for processing' : 'Preparing preview';
  const statusSub = showFailed
    ? `${failureMessage}. Preview processing stopped. Check the source file; if it opens correctly, contact support before uploading another copy.`
    : processing
      ? (recovering ? 'A retry may be available. Waiting for a confirmed processing update; do not upload another copy.' : 'You can keep browsing while we prepare this photo.')
      : retrying
        ? 'Refreshing preview…'
        : null;

  if (viewMode === 'list') {
    return (
      <img
        ref={tileImageRef}
        src={displaySrc}
        alt={img.title || 'Photo'}
        loading="lazy"
        onError={handleError}
        onLoad={handleLoad}
      />
    );
  }

  return (
    <div className="absolute inset-0">
      {!loaded && <div className="tile-shimmer" />}
      <img
        ref={tileImageRef}
        src={displaySrc}
        alt={img.title || 'Photo'}
        loading="lazy"
        className={`absolute inset-0 object-cover w-full h-full ${loaded ? 'tile-loaded' : 'tile-loading'}`}
        onError={handleError}
        onLoad={handleLoad}
      />
      {showSpinner && (
        <div className="photo-processing-overlay" title={failed ? failureMessage : undefined}>
          <div className="photo-processing-pill" title={statusText} aria-label={statusText}>
            {!failed && (processing || retrying) && <span className="processing-spinner" />}
            <span className="photo-processing-title">{compactStatusText}</span>
          </div>
          {statusSub && !showFailed && <div className="photo-processing-sub">{statusSub}</div>}
          {showFailed && <button type="button" className="photo-status-info" aria-label="Photo processing details" onClick={event => { event.stopPropagation(); onDetails?.(img.id); }}><FiInfo aria-hidden="true" /> Details</button>}
          {showFailed && onRetry && normalizeProcessingError(img.processing_error)?.retryable === true && (
            <button
              className="processing-retry-btn"
              onClick={(e) => { e.stopPropagation(); onRetry(img.id); }}
            >
              Retry
            </button>
          )}
        </div>
      )}
      {processing && loaded && !failed && (
        <div className="photo-processing-chip" title={chipStatusText} aria-label={chipStatusText}>
          <span className="processing-spinner" />
          <span className="photo-processing-title">{img.processing_status === 'queued' && !recovering ? 'Queued' : chipStatusText}</span>
        </div>
      )}
    </div>
  );
}

export default function ImageGrid({
  images,
  onRefresh,
  viewMode: propViewMode,
  selectionMode: propSelectionMode,
  selectedImages: propSelectedImages,
  onSelectionChange,
  albumId = null,
  tileSize = 200,
  showProcessingToast = false,
  useFastThumbnails: propUseFastThumbnails = null
}) {
  const { user } = useAuth();
  const features = getFeatureFlags(user);
  const stripEnabled = features.strip;
  // Allow prop to override feature flag (used for filtered views)
  const useFastThumbnails = propUseFastThumbnails ?? features.fastThumbnails;
  const isMobile = useIsMobile();
  const lastTap = useRef(0);
  // Use external state if provided, otherwise use local state
  const [localSelectedImages, setLocalSelectedImages] = useState([]);
  const [localSelectionMode, setLocalSelectionMode] = useState(false);
  const [showImageInfo, setShowImageInfo] = useState(false); // Toggle for desktop info panel

  const selectedImages = propSelectedImages !== undefined ? propSelectedImages : localSelectedImages;
  const selectionMode = propSelectionMode !== undefined ? propSelectionMode : localSelectionMode;
  const setSelectedImages = onSelectionChange || setLocalSelectedImages;
  const setSelectionMode = propSelectionMode !== undefined ?
    (mode) => { } : // Don't set local state if using external
    setLocalSelectionMode;
  const [showTagManager, setShowTagManager] = useState(false);
  const [isEditingFilename, setIsEditingFilename] = useState(false);
  const [showAlbumAdder, setShowAlbumAdder] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'contact'
  const [localImages, setLocalImages] = useState(Array.isArray(images) ? images : []);
  const [unconfirmedIds, setUnconfirmedIds] = useState([]);
  const [detailsImageId, setDetailsImageId] = useState(null);
  const [statusUpdatesInterrupted, setStatusUpdatesInterrupted] = useState(false);
  useEffect(() => {
    setUnconfirmedIds([]);
    setStatusUpdatesInterrupted(false);
  }, [user?.id]);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showMobileViewer, setShowMobileViewer] = useState(false);
  const [mobileViewerIndex, setMobileViewerIndex] = useState(0);
  const [rotating, setRotating] = useState(false);
  const [readySrc, setReadySrc] = useState(null);
  const [hiResFailed, setHiResFailed] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [hiResVariant, setHiResVariant] = useState('xl');
  const [transitionDirection, setTransitionDirection] = useState(1);
  const [filenameDraft, setFilenameDraft] = useState('');
  const [infoPosition, setInfoPosition] = useState({ x: 0, y: 0 });
  const infoDragRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const lightboxImageRef = useRef(null);
  const lightboxRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const [controlsActive, setControlsActive] = useState(true);
  const controlsTimer = useRef(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCaption, setShareCaption] = useState('');
  const [sharing, setSharing] = useState(false);
  const [processingIds, setProcessingIds] = useState([]);
  const [processingQueue, setProcessingQueue] = useState(null);
  const hadProcessingRef = useRef(false);
  const recoveredIdsRef = useRef(new Set());
  const markOriginFallback = useCallback((imageId) => {
    if (!imageId) return;
    const bump = Date.now();
    setLocalImages((prev) => prev.map((img) => {
      if (img.id !== imageId || img._force_origin) return img;
      return { ...img, _force_origin: true, _v: bump };
    }));
    if (lightboxImage?.id === imageId && !lightboxImage?._force_origin) {
      setLightboxImage((prev) => (prev && prev.id === imageId ? { ...prev, _force_origin: true, _v: bump } : prev));
    }
  }, [lightboxImage?.id, lightboxImage?._force_origin]);
  const hoverPrefetchRef = useRef(new Set());
  const prefetchHiRes = useCallback((img) => {
    if (!img || isProcessing(img)) return;
    const key = buildVariantUrl(img, 'xl');
    if (!key || hoverPrefetchRef.current.has(key)) return;
    hoverPrefetchRef.current.add(key);
    photoCache.prefetchVariants([img], { variants: ['xl'], concurrency: 1 }).catch(() => { });
  }, []);

  useEffect(() => {
    if (!lightboxImage) return;
    setHiResFailed(false);
    setHiResVariant('xl');
  }, [lightboxImage?.id, lightboxImage?._v]);

  const lightboxProcessing = isProcessing(lightboxImage);
  const lightboxVariants = usePhotoVariants(lightboxImage, { variants: [hiResVariant], allowNetwork: !lightboxProcessing });

  const previewSrc = useMemo(() => {
    if (!lightboxImage) return '';
    if (lightboxProcessing) return PROCESSING_PLACEHOLDER;
    return lightboxImage._previewSrc || buildVariantUrl(lightboxImage, useFastThumbnails ? 'sm' : 'md') || '';
  }, [lightboxImage?.id, lightboxImage?._v, lightboxProcessing, useFastThumbnails]);

  // Wait for the cache lookup before assigning src. A network fallback here
  // would download the same image in parallel with the cache loader.
  const hiResSrc = lightboxProcessing ? '' : lightboxVariants[hiResVariant] || '';
  // A late metadata effect must never hide an image whose load already fired.
  const hiResReady = Boolean(hiResSrc && readySrc === hiResSrc);

  useLayoutEffect(() => {
    const img = lightboxImageRef.current;
    // Cached native images may already be complete when React attaches onLoad.
    if (hiResSrc && img?.getAttribute('src') === hiResSrc && img.complete && img.naturalWidth > 0) {
      setReadySrc(hiResSrc);
    }
  }, [hiResSrc]);

  useEffect(() => {
    if (!lightboxImage || !hiResReady || localImages.length === 0) return;
    const total = localImages.length;
    const radius = 2;
    const ordered = [];

    for (let i = 1; i <= radius; i += 1) {
      ordered.push(localImages[(lightboxIndex + i) % total]);
    }
    for (let i = 1; i <= radius; i += 1) {
      ordered.push(localImages[(lightboxIndex - i + total) % total]);
    }

    const seen = new Set();
    const unique = ordered.filter((img) => {
      if (!img || img.id === lightboxImage.id || seen.has(img.id)) return false;
      seen.add(img.id);
      return true;
    });

    const ready = unique.filter((img) => !isProcessing(img));
    photoCache.prefetchVariants(ready, { variants: ['xl'], concurrency: 2 }).catch(() => { });
  }, [lightboxImage?.id, lightboxImage?._v, hiResReady, lightboxIndex, localImages.length]);

  // Detect if we're on mobile
  const isViewerCompact = useIsMobile(1024);

  // Build simplified EXIF object for UI from image.exif_data
  const buildUiExif = (img) => {
    try {
      const raw = img?.exif_data ? (typeof img.exif_data === 'string' ? JSON.parse(img.exif_data) : img.exif_data) : null;
      if (!raw || typeof raw !== 'object') return null;

      // Helper to get value from multiple possible field names
      const getField = (...keys) => {
        for (const k of keys) {
          if (raw[k] !== undefined && raw[k] !== null && raw[k] !== '') return raw[k];
        }
        return null;
      };

      // Camera: try multiple field name variations
      const make = getField('Make', 'make', 'CameraMake');
      const model = getField('Model', 'model', 'CameraModel');
      let camera = null;
      if (make && model) {
        // Avoid duplication like "Canon Canon EOS R5"
        camera = model.toLowerCase().startsWith(make.toLowerCase()) ? model : `${make} ${model}`;
      } else {
        camera = model || make || null;
      }

      // Lens
      const lens = getField('LensModel', 'Lens', 'LensInfo', 'LensID');

      // ISO
      const iso = getField('ISO', 'ISOSpeedRatings', 'PhotographicSensitivity');

      // Aperture
      const fNum = getField('FNumber', 'ApertureValue', 'Aperture');
      const aperture = fNum ? parseFloat(fNum).toFixed(1) : null;

      // Focal length
      const fl = getField('FocalLength', 'FocalLengthIn35mmFormat', 'FocalLengthIn35mmFilm');
      const focal = fl ? Math.round(parseFloat(fl)) : null;

      // Shutter speed
      let shutter = null;
      const expTime = getField('ExposureTime', 'ShutterSpeedValue', 'ShutterSpeed');
      if (expTime) {
        const t = parseFloat(expTime);
        shutter = isFinite(t) ? (t >= 1 ? `${t}` : `1/${Math.round(1 / Math.max(t, 1e-6))}`) : null;
      }

      // Date taken
      const takenAt = getField('DateTimeOriginal', 'CreateDate', 'DateTime', 'ModifyDate') || img?.taken_at || null;

      // Only return object if we have at least one piece of useful data
      const hasData = camera || lens || iso || aperture || focal || shutter || takenAt;
      return hasData ? { camera, lens, aperture, shutter_speed: shutter, iso, focal_length: focal, taken_at: takenAt, _raw: raw } : null;
    } catch { return null; }
  };

  // Touch handling for mobile
  const touchStartRef = React.useRef({ x: 0, y: 0, time: 0, target: null });

  // Update local images when props change
  React.useEffect(() => {
    const uniq = (list) => {
      const seen = new Set();
      return (Array.isArray(list) ? list : []).filter((img) => {
        const k = img?.id || img?.filename;
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };
    setLocalImages(uniq(images));
  }, [images]);

  // Track processing items to poll their status
  useEffect(() => {
    const ids = localImages
      .filter((img) => isProcessing(img))
      .map((img) => img.id)
      .filter(Boolean);
    setProcessingIds(ids);
    if (ids.length === 0) {
      setProcessingQueue(null);
    }
  }, [localImages]);

  // Poll processing status (Burst/variants) and update tiles
  useEffect(() => {
    if (!processingIds.length) return () => {};
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch('/api/images/processing/status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ image_ids: processingIds })
        });
        if (!res.ok) { if (!cancelled) setStatusUpdatesInterrupted(true); return; }
        const data = await res.json();
        const statuses = data.images || {};
        if (cancelled) return;
        setStatusUpdatesInterrupted(false);
        setUnconfirmedIds((previous) => {
          const next = new Set(previous);
          Object.entries(statuses).forEach(([id, status]) => {
            if (status.status === 'not_found') next.add(id);
            else if (status.status) next.delete(id);
          });
          return [...next];
        });
        setProcessingQueue(data.queue || null);
        setLocalImages((prev) => prev
          // Filter out images that don't exist in the database (ghost/orphaned uploads)
          .filter((img) => {
            const status = statuses[img.id];
            return !status || status.status !== 'not_found';
          })
          .map((img) => {
            const status = statuses[img.id];
            if (!status) return img;
            const nextStatus = status.status || img.processing_status;
            const next = {
              ...img,
              processing_status: nextStatus
            };
            if (status.error) {
              next.processing_error = status.error_detail || { message: status.error, retryable: status.error_retryable === true };
              next.processing_retry_count = status.retry_count ?? img.processing_retry_count;
            }
            if (nextStatus === 'ready') {
              next.processing_error = null;
              next.processing_eta_ms = null;
            } else if (nextStatus === 'failed') {
              next.processing_eta_ms = null;
            } else if (status.estimated_ms !== undefined) {
              next.processing_eta_ms = status.estimated_ms;
            }
            if (status.signature) {
              next._v = status.signature;
            } else if (nextStatus !== img.processing_status && nextStatus === 'ready') {
              next._v = Date.now();
            }
            return next;
          }));
      } catch {
        if (!cancelled) setStatusUpdatesInterrupted(true);
      }
    };

    poll();
    const t = setInterval(poll, 1000); // Poll every 1s for faster updates
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [processingIds.join(',')]);

  const handlePreviewRecovered = React.useCallback((imageId, options = {}) => {
    if (!imageId) return;
    const forceReady = options?.forceReady;

    if (forceReady) {
      setLocalImages((prev) => prev.map((img) => {
        if (img.id !== imageId) return img;
        return {
          ...img,
          processing_status: 'ready',
          processing_error: null,
          processing_eta_ms: null,
          _v: Date.now()
        };
      }));
    }

    if (recoveredIdsRef.current.has(imageId)) return;
    recoveredIdsRef.current.add(imageId);

    fetch('/api/images/processing/status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ image_ids: [imageId] })
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const status = data?.images?.[imageId];
        if (!status || status.status !== 'ready') return;
        setLocalImages((prev) => prev.map((img) => {
          if (img.id !== imageId) return img;
          return {
            ...img,
            processing_status: 'ready',
            processing_eta_ms: null,
            _v: status.signature || Date.now()
          };
        }));
      })
      .catch(() => {})
      .finally(() => {
        setTimeout(() => recoveredIdsRef.current.delete(imageId), 60000);
      });
  }, []);

  // Use prop selection mode if provided, otherwise use local state
  const currentSelectionMode = propSelectionMode !== undefined ? propSelectionMode : selectionMode;
  const currentSelectedImages = propSelectedImages !== undefined ? propSelectedImages : selectedImages;
  const updateSelectedImages = onSelectionChange || setSelectedImages;

  function toggleSelection(imageId) {
    updateSelectedImages(prev =>
      prev.includes(imageId)
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  }

  function selectAll() {
    setSelectedImages(localImages.map(img => img.id));
  }

  function clearSelection() {
    setSelectedImages([]);
    setLocalSelectionMode(false);
  }

  useEffect(() => {
    if (showAlbumAdder) {
      fetch('/api/albums', { credentials: 'include' })
        .then(r => r.json())
        .then(setAlbums);
    }
  }, [showAlbumAdder]);

  async function addToAlbum(albumId) {
    for (const imageId of selectedImages) {
      await fetch(`/api/albums/${albumId}/images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_id: imageId })
      });
    }
    setShowAlbumAdder(false);
    clearSelection();
    if (onRefresh) onRefresh();
  }

  async function rotateImage(imageId, direction = 'right') {
    if (rotating) return;
    setRotating(true);

    const angle = direction === 'left' ? -90 : 90;
    const target = localImages.find(img => img.id === imageId);
    const ver = Date.now();

    // Calculate new rotation (optimistic)
    const currentRotation = target?._localRotation || 0;
    const newRotation = currentRotation + angle;

    // Optimistic update - apply rotation immediately via CSS transform
    setLocalImages(prev => prev.map(img =>
      img.id === imageId ? { ...img, _localRotation: newRotation, _v: ver } : img
    ));

    if (lightboxImage && lightboxImage.id === imageId) {
      setLightboxImage(prev => ({ ...prev, _localRotation: newRotation, _v: ver }));
    }

    // Fire API in background
    try {
      const res = await apiFetch(`/api/images/${imageId}/rotate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ angle })
      });

      if (res.ok) {
        const updated = await res.json();
        const finalVer = Date.now();

        // Evict old cache entries before preloading new image
        photoCache.evictImage({ id: imageId, filename: target?.filename }).catch(() => { });
        invalidateImageCache('rotate', { imageId });

        // Preload the new rotated image BEFORE clearing CSS rotation
        // This prevents the visual "jump" - we keep CSS rotation until new image is ready
        const newImageUrl = updated.cdn_urls?.xl || updated.cdn_urls?.lg || buildVariantUrl(updated, 'xl');

        if (newImageUrl) {
          // Preload the new image
          const preloadImg = new Image();
          await new Promise((resolve) => {
            preloadImg.onload = resolve;
            preloadImg.onerror = resolve; // Continue even on error
            preloadImg.src = newImageUrl;
            // Timeout fallback - don't wait forever
            setTimeout(resolve, 5000);
          });
        }

        // NOW swap to the server image - user sees a clean transition
        setLocalImages(prev => prev.map(img =>
          img.id === imageId ? { ...img, ...updated, _localRotation: 0, _v: finalVer } : img
        ));

        if (lightboxImage && lightboxImage.id === imageId) {
          setLightboxImage(prev => ({ ...prev, ...updated, _localRotation: 0, _v: finalVer }));
        }

        // Invalidate cache
        if (user) {
          cacheStrategy.invalidate('edit', { userId: getPrivateCacheScope().scope, imageId, albumId });
        }
      } else {
        // Revert on failure
        setLocalImages(prev => prev.map(img =>
          img.id === imageId ? { ...img, _localRotation: currentRotation, _v: ver } : img
        ));
        if (lightboxImage && lightboxImage.id === imageId) {
          setLightboxImage(prev => ({ ...prev, _localRotation: currentRotation, _v: ver }));
        }
      }
    } catch (error) {
      console.error('Rotation failed', error);
      // Revert on error
      setLocalImages(prev => prev.map(img =>
        img.id === imageId ? { ...img, _localRotation: currentRotation, _v: ver } : img
      ));
      if (lightboxImage && lightboxImage.id === imageId) {
        setLightboxImage(prev => ({ ...prev, _localRotation: currentRotation, _v: ver }));
      }
    } finally {
      setRotating(false);
    }
  }

  // Draggable info panel handlers
  const startInfoDrag = (e) => {
    e.preventDefault();
    infoDragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: infoPosition.x,
      originY: infoPosition.y
    };
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (!infoDragRef.current.active) return;
      const dx = e.clientX - infoDragRef.current.startX;
      const dy = e.clientY - infoDragRef.current.startY;
      const nextX = infoDragRef.current.originX + dx;
      const nextY = infoDragRef.current.originY + dy;
      setInfoPosition({
        x: Math.min(Math.max(nextX, -window.innerWidth * 0.4), window.innerWidth * 0.4),
        y: Math.min(Math.max(nextY, -window.innerHeight * 0.4), window.innerHeight * 0.4)
      });
    };
    const handleUp = () => {
      infoDragRef.current.active = false;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [infoPosition.x, infoPosition.y]);

  // Fullscreen tracking
  useEffect(() => {
    const handler = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (!lightboxImage) return;
      // Avoid interfering with inputs
      const target = e.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (e.key === 'f') {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        applyZoom(0.25);
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        applyZoom(-0.25);
      } else if (e.key === '0') {
        e.preventDefault();
        resetZoomPan();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxImage]);

  // Controls auto-dim on inactivity
  useEffect(() => {
    const bumpControls = () => {
      setControlsActive(true);
      if (controlsTimer.current) clearTimeout(controlsTimer.current);
      controlsTimer.current = setTimeout(() => setControlsActive(false), 1800);
    };
    bumpControls();
    const node = lightboxRef.current;
    if (!node) return;
    node.addEventListener('mousemove', bumpControls);
    node.addEventListener('mouseenter', bumpControls);
    return () => {
      node.removeEventListener('mousemove', bumpControls);
      node.removeEventListener('mouseenter', bumpControls);
      if (controlsTimer.current) clearTimeout(controlsTimer.current);
    };
  }, [lightboxRef]);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => { });
      return;
    }
    if (lightboxRef.current?.requestFullscreen) {
      lightboxRef.current.requestFullscreen().catch(() => { });
    }
  };

  const clampZoom = (z) => Math.min(4, Math.max(1, z));

  const applyZoom = (delta, focus = null) => {
    setZoom((prev) => {
      const next = clampZoom(prev + delta);
      if (!focus || !lightboxImageRef.current) {
        return next;
      }
      // Adjust pan to keep focus point stable
      const rect = lightboxImageRef.current.getBoundingClientRect();
      const fx = focus.x - rect.left - rect.width / 2 - pan.x;
      const fy = focus.y - rect.top - rect.height / 2 - pan.y;
      const scaleChange = next / prev;
      setPan((p) => ({
        x: p.x - fx * (scaleChange - 1),
        y: p.y - fy * (scaleChange - 1)
      }));
      return next;
    });
  };

  const resetZoomPan = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Reset per-image toggles on image change
  useEffect(() => {
    resetZoomPan();
  }, [lightboxImage?.id]);

  async function deleteSelected() {
    if (!confirm(`Delete ${selectedImages.length} images? This cannot be undone.`)) return;
    // Optimistically remove from UI immediately to avoid broken placeholders
    const idsToDelete = [...selectedImages];
    // Capture filenames for SW eviction
    const filesToEvict = localImages.filter(img => idsToDelete.includes(img.id)).map(img => img.filename);
    setLocalImages(prev => prev.filter(img => !idsToDelete.includes(img.id)));

    photoCache.evictByFilenames(filesToEvict).catch(() => { });

    // Batch delete on the server (limited concurrency, single request)
    await apiFetch('/api/images/batch-delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: idsToDelete })
    });

    // Invalidate caches after bulk delete
    if (user) {
      selectedImages.forEach(imageId => {
        cacheStrategy.invalidate('delete', {
          userId: getPrivateCacheScope().scope,
          imageId,
          albumId
        });
      });
    }

    // Invalidate image preloader cache for batch delete
    invalidateImageCache('batchDelete', { imageIds: idsToDelete });

    // Ask SW to evict cached media for these filenames
    try {
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        filesToEvict.forEach((fn) => {
          navigator.serviceWorker.controller.postMessage({
            type: 'EVICT_MEDIA',
            filename: fn,
            base: fn.replace(/\.[^.]+$/, '')
          });
        });
      }
    } catch { }

    clearSelection();
    if (onRefresh) onRefresh(true); // Force refresh to reconcile any failures
  }

  async function deleteImage(imageId, e) {
    if (e) e.stopPropagation();
    if (!confirm('Delete this image? This cannot be undone.')) return false;
    // Optimistically remove from UI first to avoid placeholders
    const fileToEvict = localImages.find(img => img.id === imageId)?.filename;
    setLocalImages(prev => prev.filter(img => img.id !== imageId));
    if (fileToEvict) {
      photoCache.evictByFilenames([fileToEvict]).catch(() => { });
    }

    // Close lightbox if this was the active image
    if (lightboxImage && lightboxImage.id === imageId) {
      setLightboxImage(null);
    }

    // Fire the delete request in background
    apiFetch(`/api/images/${imageId}`, {
      method: 'DELETE'
    })
      .then(() => {
        if (user) {
          cacheStrategy.invalidate('delete', { userId: getPrivateCacheScope().scope, imageId, albumId });
        }
        // Invalidate image preloader cache
        invalidateImageCache('delete', { imageId });
        // Evict SW cached media
        try {
          if (fileToEvict && navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'EVICT_MEDIA',
              filename: fileToEvict,
              base: fileToEvict.replace(/\.[^.]+$/, '')
            });
          }
        } catch { }
        if (onRefresh) onRefresh(true);
      })
      .catch((error) => {
        console.error('Failed to delete image:', error);
        // Optional: could trigger a silent reconcile by calling onRefresh
        if (onRefresh) onRefresh(true);
      });
  }

  async function toggleLike(imageId, currentlyLiked) {
    const newFavoriteState = !currentlyLiked;

    // Update local state immediately for responsive UI
    setLocalImages(prev => prev.map(img =>
      img.id === imageId ? { ...img, is_favorite: newFavoriteState } : img
    ));

    // Update lightbox image if it's the one being liked
    if (lightboxImage && lightboxImage.id === imageId) {
      setLightboxImage(prev => ({ ...prev, is_favorite: newFavoriteState }));
    }

    try {
      await api.patch(`/api/images/${imageId}`, { is_favorite: newFavoriteState });

      // Invalidate caches affected by like/unlike
      if (user) {
        cacheStrategy.invalidate(newFavoriteState ? 'like' : 'unlike', {
          userId: getPrivateCacheScope().scope,
          imageId,
          albumId
        });
      }

      // Invalidate image preloader cache  
      invalidateImageCache(newFavoriteState ? 'like' : 'unlike', { imageId });

      // Refresh parent data in background
      if (onRefresh) onRefresh(true); // Force refresh to get fresh data
    } catch (error) {
      console.error('Failed to toggle like:', error);
      // Revert local state on error
      setLocalImages(prev => prev.map(img =>
        img.id === imageId ? { ...img, is_favorite: currentlyLiked } : img
      ));
      if (lightboxImage && lightboxImage.id === imageId) {
        setLightboxImage(prev => ({ ...prev, is_favorite: currentlyLiked }));
      }
    }
  }

  async function hydrateImageDetails(image) {
    if (!image) return image;
    const needsTags = typeof image.tags === 'undefined';
    const needsExif = image.exif_data === undefined || image.exif_data === null;
    const needsVariants = !image._cdn_full && (!image.cdn_urls || !image.cdn_urls.xl);

    if (!needsTags && !needsExif && !needsVariants) return image;

    const params = new URLSearchParams();
    const include = [];
    if (needsTags) include.push('tags');
    if (needsExif) include.push('exif');
    if (include.length) params.set('include', include.join(','));
    if (needsVariants) params.set('variants', 'xl,lg');

    try {
      const url = params.toString()
        ? `/api/images/${image.id}?${params.toString()}`
        : `/api/images/${image.id}`;
      const res = await apiFetch(url);
      if (!res.ok) return image;
      const data = await res.json();
      // Metadata hydration does not change the image bytes or their cache version.
      const bump = image._v;
      const merged = { ...image, ...data, cdn_urls: { ...image.cdn_urls, ...data.cdn_urls }, _cdn_full: needsVariants ? true : image._cdn_full, _v: bump };
      setLocalImages(prev => prev.map(img => img.id === image.id ? { ...img, ...data, cdn_urls: merged.cdn_urls, _cdn_full: merged._cdn_full, _v: bump } : img));
      return merged;
    } catch (error) {
      console.error('Failed to fetch image details:', error);
      return image;
    }
  }

  function withPreview(image) {
    const tile = document.querySelector(`[data-image-id="${CSS.escape(String(image.id))}"] img`);
    return { ...image, _v: getPhotoVersion(image), _previewSrc: tile?.complete && tile.naturalWidth ? tile.currentSrc || tile.src : undefined };
  }

  async function openLightbox(image) {
    image = withPreview(image);
    const index = localImages.findIndex(img => img.id === image.id);

    if (isViewerCompact) {
      setMobileViewerIndex(index);
      setShowMobileViewer(true);
    } else {
      setTransitionDirection(1);
      setPreviewReady(false);
      setHiResFailed(false);
      setHiResVariant('xl');
      setLightboxImage(image);
      setLightboxIndex(index);
      const hydrated = await hydrateImageDetails(image);
      setLightboxImage((prev) => (prev?.id === image.id ? hydrated : prev));
    }
  }

  function closeLightbox() {
    setLightboxImage(null);
    setEditingTitle(false);
    setEditTitle('');
    setViewMode('single');
  }

  function startEditingTitle() {
    setEditingTitle(true);
    setEditTitle(lightboxImage.title || lightboxImage.filename || '');
  }

  function cancelEditTitle() {
    setEditingTitle(false);
    setEditTitle('');
  }

  async function saveTitle() {
    try {
      const response = await api.patch(`/api/images/${lightboxImage.id}`, { title: editTitle });

      if (response.ok) {
        // Update local state
        setLocalImages(prev => prev.map(img =>
          img.id === lightboxImage.id ? { ...img, title: editTitle } : img
        ));
        setLightboxImage(prev => ({ ...prev, title: editTitle }));
        setEditingTitle(false);
        setEditTitle('');
      }
    } catch (error) {
      console.error('Failed to update title:', error);
    }
  }

  async function navigateLightbox(direction) {
    if (!lightboxImage) return;

    const currentIndex = localImages.findIndex(img => img.id === lightboxImage.id);
    let newIndex = currentIndex + direction;

    if (newIndex < 0) newIndex = localImages.length - 1;
    if (newIndex >= localImages.length) newIndex = 0;

    const nextImage = withPreview(localImages[newIndex]);
    setTransitionDirection(direction);
    setPreviewReady(false);
    setHiResFailed(false);
    setHiResVariant('xl');
    setLightboxImage(nextImage);
    setLightboxIndex(newIndex);
    const hydrated = await hydrateImageDetails(nextImage);
    setLightboxImage((prev) => (prev?.id === nextImage?.id ? hydrated : prev));
    setEditingTitle(false);
    setEditTitle('');
  }

  // Keyboard navigation
  React.useEffect(() => {
    if (!lightboxImage) return;

    function handleKeyDown(e) {
      if (e.key === 'ArrowLeft') {
        navigateLightbox(-1);
      } else if (e.key === 'ArrowRight') {
        navigateLightbox(1);
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, localImages]);

  // Touch handling for mobile photo tiles
  React.useEffect(() => {
    if (!isViewerCompact) return;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      const photoTile = e.target.closest('.photo-tile');
      if (photoTile) {
        touchStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: Date.now(),
          target: photoTile
        };
      }
    };

    const handleTouchEnd = (e) => {
      const touch = e.changedTouches[0];
      const photoTile = e.target.closest('.photo-tile');

      if (photoTile && photoTile === touchStartRef.current.target) {
        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
        const deltaTime = Date.now() - touchStartRef.current.time;

        // Consider it a tap if movement is less than 10px and time is less than 300ms
        if (deltaX < 10 && deltaY < 10 && deltaTime < 300) {
          const index = parseInt(photoTile.dataset.index);
          if (!isNaN(index) && !currentSelectionMode) {
            setMobileViewerIndex(index);
            setShowMobileViewer(true);
          }
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isViewerCompact, currentSelectionMode]);

  // Ensure we don't render duplicates (defensive against any upstream duplication)
  const displayImages = React.useMemo(() => {
    const seen = new Set();
    return (localImages || []).filter(img => {
      const k = (img?.filename || '').toLowerCase();
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [localImages]);

  // Drag-to-select
  const gridRef = useRef(null);
  const { lassoRef, isDragSelecting, startDrag } = useDragSelect({
    containerRef: gridRef,
    onSelectionChange: (ids) => {
      updateSelectedImages(ids);
    },
    enabled: currentSelectionMode && !isMobile,
  });

  const processingCounts = useMemo(() => {
    const counts = { total: 0, failed: 0 };
    displayImages.forEach((img) => {
      if (isFailed(img)) counts.failed += 1;
      if (isProcessing(img)) {
        counts.total += 1;
      }
    });
    return counts;
  }, [displayImages]);

  useEffect(() => {
    if (!showProcessingToast) return;
    if (processingCounts.total > 0) {
      hadProcessingRef.current = true;
      return;
    }
    if (processingCounts.total === 0 && hadProcessingRef.current) {
      const needsAttention = processingCounts.failed > 0 || unconfirmedIds.length > 0;
      const title = needsAttention ? 'Check your uploads' : 'Processing complete';
      const detail = unconfirmedIds.length > 0
        ? 'Some upload records could not be confirmed. Check the library message before uploading another copy.'
        : processingCounts.failed > 0
        ? `${processingCounts.failed} photo${processingCounts.failed !== 1 ? 's need' : ' needs'} attention.`
        : 'Your photos are ready.';
      showToast(title, detail, needsAttention ? 'warning' : 'success');
      hadProcessingRef.current = false;
    }
  }, [processingCounts.total, processingCounts.failed, showProcessingToast, unconfirmedIds.length]);

  const handleRetryOne = async (imageId) => {
    try {
      const res = await fetch(`/api/images/${imageId}/reprocess`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setLocalImages((prev) =>
          prev.map((img) =>
            img.id === imageId ? { ...img, processing_status: 'processing', processing_error: null } : img
          )
        );
      }
    } catch {}
  };


  return (
    <>
      {detailsImageId && localImages.some(image => image.id === detailsImageId) && (
        <PhotoProcessingDetails key={detailsImageId} image={localImages.find(image => image.id === detailsImageId)} onClose={() => setDetailsImageId(null)} onRemove={deleteImage} onCheck={async id => {
          const response = await fetch('/api/images/processing/status', {method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({image_ids:[id]})});
          if (!response.ok) throw new Error('Status unavailable');
          const data = await response.json(); const status = data.images?.[id];
          if (!status || status.status === 'not_found') throw new Error('Status unavailable');
          setLocalImages(previous => previous.map(image => image.id === id ? {...image, processing_status:status.status, processing_error:status.status === 'ready' ? null : (status.error_detail || (status.error ? {message:status.error,retryable:status.error_retryable===true} : null))} : image));
        }} />
      )}
      {unconfirmedIds.length > 0 && (
        <div className="processing-summary" role="alert">
          <strong>Upload confirmation unavailable</strong>
          <p>{unconfirmedIds.length} upload record{unconfirmedIds.length === 1 ? '' : 's'} could not be confirmed in this library. This does not tell us whether the original is stored. Refresh the library and check your account before uploading another copy. If it is still missing, contact support.</p>
          <button type="button" onClick={() => { if (onRefresh) onRefresh(); else window.location.reload(); }}>Refresh library</button>
          <button type="button" onClick={() => setUnconfirmedIds([])}>Dismiss confirmation warning</button>
        </div>
      )}
      {statusUpdatesInterrupted && processingCounts.total > 0 && (
        <div className="processing-summary" role="status">
          Status updates interrupted. Processing may still be running. We will check again automatically; check your connection or refresh the library before uploading another copy.
        </div>
      )}
      {(processingCounts.total > 0 || processingCounts.failed > 0) && (
        <div className="processing-summary">
          {processingCounts.total > 0 && (
            <div className="processing-summary-item">
              <span className="processing-spinner" />
              <span>Waiting for previews</span>
            </div>
          )}
          {processingCounts.total > 0 && (
            <div className="processing-summary-item">
              <span>Processing has not finished. You can keep browsing; status updates will appear here. If it stays unchanged, refresh the library or contact support before uploading another copy.</span>
            </div>
          )}
          {processingCounts.failed > 0 && (
            <div className="processing-summary-item processing-failed">
              <span>
                {processingCounts.failed} photo{processingCounts.failed !== 1 ? 's need' : ' needs'} attention.
              </span>
              <span>Check the photo’s message for the next step.</span>
            </div>
          )}
        </div>
      )}

      <div
        ref={gridRef}
        className="grid gap-3 md:gap-4 pt-3 md:pt-4"
        style={{
          gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '45%' : `${tileSize}px`}, 1fr))`
        }}
      >
        {displayImages.map((img, index) => (
          <div
            className={`relative overflow-hidden rounded-md cursor-pointer aspect-square group ${currentSelectedImages.includes(img.id) ? 'ring-2 ring-blue-500' : ''} ${img.is_favorite ? 'ring-2 ring-[#d9c86a]' : ''}`}
            key={img.id}
            data-image-id={img.id}
            data-index={index}
            onMouseEnter={() => prefetchHiRes(img)}
            onTouchStart={() => prefetchHiRes(img)}
            onTouchEnd={(e) => {
              const currentTime = new Date().getTime();
              const tapLength = currentTime - lastTap.current;
              if (tapLength < 300 && tapLength > 0) {
                toggleLike(img.id, img.is_favorite);
                e.preventDefault();
              }
              lastTap.current = currentTime;
            }}
            onClick={(e) => {
              if (isDragSelecting) return;
              if (currentSelectionMode) {
                toggleSelection(img.id);
              } else {
                // Open lightbox/mobile viewer
                openLightbox(img);
              }
            }}
          >
            {currentSelectionMode && (
              <>
                <div className="absolute top-2 left-2 z-10">
                  <input
                    type="checkbox"
                    checked={currentSelectedImages.includes(img.id)}
                    onChange={() => { }}
                    className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div
                  className="drag-select-handle"
                  onMouseDown={(e) => startDrag(e, img.id, currentSelectedImages)}
                />
              </>
            )}
            {!currentSelectionMode && (
              <>
                <div
                  className="absolute top-2 right-2 z-10 p-1.5 bg-black bg-opacity-50 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.currentTarget.classList.add('clicked');
                    setTimeout(() => {
                      e.currentTarget.classList.remove('clicked');
                    }, 300);
                    toggleLike(img.id, img.is_favorite);
                  }}
                >
                  {img.is_favorite ?
                    <FaHeart style={{ color: 'white', fontSize: '16px' }} /> :
                    <FiHeart style={{ color: 'white', fontSize: '16px' }} />
                  }
                </div>
                {img.tags && img.tags.length > 0 && (
                  <div className="absolute top-2 left-2 z-10 flex items-center gap-1 px-2 py-1 text-xs text-white bg-black bg-opacity-50 rounded-full">
                    <FiTag size={14} />
                    <span>{img.tags.length}</span>
                  </div>
                )}
              </>
            )}
            {img.original_format && (
              <div className="photo-tile-format-badge" title={`Original format: ${img.original_format}${img.creation_tool ? ` • Created with: ${img.creation_tool}` : ''}`}>
                {img.file_extension}
              </div>
            )}
            <PhotoTileImage
              img={img}
              viewMode="grid"
              onPreviewRecovered={handlePreviewRecovered}
              onCdnFallback={markOriginFallback}
              useFastThumbnails={useFastThumbnails}
              onRetry={handleRetryOne}
              onDetails={setDetailsImageId}
            />
          </div>
        ))}
      </div>

      <div ref={lassoRef} className="lasso-rect" style={{ display: 'none' }} />

      {showAlbumAdder && (
        <div className="modal-overlay" onClick={() => setShowAlbumAdder(false)}>
          <div className="modal-content album-selector" onClick={(e) => e.stopPropagation()}>
            <div className="album-selector-header">
              <h2>Add to Album</h2>
              <p className="album-selector-subtitle">
                Select an album to add {selectedImages.length} {selectedImages.length === 1 ? 'photo' : 'photos'}
              </p>
            </div>

            {albums.length > 0 ? (
              <div className="album-list">
                {albums.map(album => (
                  <button
                    key={album.id}
                    className="album-item"
                    onClick={() => addToAlbum(album.id)}
                  >
                    <div className="album-item-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <rect x="7" y="7" width="10" height="10" rx="1" />
                      </svg>
                    </div>
                    <div className="album-info">
                      <div className="album-title">{album.title}</div>
                      <div className="album-meta">
                        <span className="album-photo-count">{album.image_count} {album.image_count === 1 ? 'photo' : 'photos'}</span>
                      </div>
                    </div>
                    <div className="album-add-action">
                      <span className="album-add-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                      </span>
                      <span className="album-add-text">Add</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="album-list-empty">
                <p className="muted">No albums available</p>
                <p className="muted small">Create an album first to organize your photos</p>
              </div>
            )}

            <div className="modal-footer">
              <button onClick={() => setShowAlbumAdder(false)} className="button-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showTagManager && (
        <div className="modal-overlay" onClick={() => setShowTagManager(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <TagManager
              selectedImages={selectedImages}
              onClose={() => {
                setShowTagManager(false);
                clearSelection();
                if (onRefresh) onRefresh();
              }}
            />
          </div>
        </div>
      )}

      {lightboxImage && (
        <div className="lightbox" ref={lightboxRef}>
          {/* Close button - top left */}
          <button className="lightbox-close-floating" onClick={closeLightbox}>
            <FiX size={22} />
          </button>

          {/* Floating dock - bottom center */}
          <div className="lightbox-dock">
            {/* Navigation group */}
            <div className="dock-group">
              <button
                className="dock-btn"
                onClick={() => navigateLightbox(-1)}
                data-tooltip="Previous"
              >
                <FiChevronLeft size={22} />
              </button>
              <span className="dock-counter">
                {localImages.findIndex(img => img.id === lightboxImage.id) + 1} / {localImages.length}
              </span>
              <button
                className="dock-btn"
                onClick={() => navigateLightbox(1)}
                data-tooltip="Next"
              >
                <FiChevronRight size={22} />
              </button>
            </div>

            <div className="dock-divider" />

            {/* View mode group */}
            <div className="dock-group">
              <button
                className={`dock-btn ${showImageInfo ? 'active' : ''}`}
                onClick={() => setShowImageInfo(!showImageInfo)}
                data-tooltip="Photo Info"
              >
                <FiInfo size={20} />
              </button>
              <button
                className={`dock-btn ${viewMode === 'contact' ? 'active' : ''}`}
                onClick={() => setViewMode(viewMode === 'single' ? 'contact' : 'single')}
                data-tooltip={viewMode === 'single' ? 'Contact Sheet' : 'Single View'}
              >
                {viewMode === 'single' ? <FiGrid size={20} /> : <FiImage size={20} />}
              </button>
            </div>

            <div className="dock-divider" />

            <div className="dock-group">
              <button
                className="dock-btn"
                onClick={() => rotateImage(lightboxImage.id, 'left')}
                disabled={rotating}
                data-tooltip="Rotate Left"
              >
                <FiRotateCw size={20} className={rotating ? 'rotating' : ''} style={{ transform: 'scaleX(-1)' }} />
              </button>
              <button
                className="dock-btn"
                onClick={() => rotateImage(lightboxImage.id, 'right')}
                disabled={rotating}
                data-tooltip="Rotate Right"
              >
                <FiRotateCw size={20} className={rotating ? 'rotating' : ''} />
              </button>
            </div>

            <div className="dock-divider" />

            <div className="dock-group">
              <button
                className="dock-btn"
                onClick={() => applyZoom(-0.25)}
                data-tooltip="Zoom Out"
              >
                <FiMinus size={20} />
              </button>
              <button
                className="dock-btn"
                onClick={() => applyZoom(0.25)}
                data-tooltip="Zoom In"
              >
                <FiPlus size={20} />
              </button>
              <button
                className="dock-btn"
                onClick={resetZoomPan}
                data-tooltip="Reset Zoom"
              >
                <FiRefreshCw size={18} />
              </button>
            </div>

            <div className="dock-divider" />

            <div className="dock-group">
              <button
                className="dock-btn"
                onClick={toggleFullscreen}
                data-tooltip={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <FiMinimize2 size={20} /> : <FiMaximize2 size={20} />}
              </button>
              {!isMobile && (
                <a
                  className="dock-btn"
                  href={`/media/originals/${lightboxImage.filename}${window?.localStorage?.getItem('auth_token') ? `?token=${encodeURIComponent(window.localStorage.getItem('auth_token'))}` : ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tooltip="Download"
                  download={lightboxImage.filename}
                >
                  <FiDownload size={20} />
                </a>
              )}
              {stripEnabled && (
                <button
                  className="dock-btn"
                  onClick={() => setShowShareModal(true)}
                  data-tooltip="Share"
                >
                  <FiShare2 size={20} />
                </button>
              )}
            </div>

            <div className="dock-divider" />

            <div className="dock-group">
              <button
                className="dock-btn dock-btn-danger"
                onClick={async () => {
                  if (!confirm('Delete this photo? This cannot be undone.')) return;
                  const toDeleteId = lightboxImage.id;
                  const toDeleteFile = lightboxImage.filename;
                  setLocalImages(prev => prev.filter(img => img.id !== toDeleteId));
                  closeLightbox();
                  try {
                    await apiFetch(`/api/images/${toDeleteId}`, { method: 'DELETE' });
                    try {
                      const { cacheStrategy } = await import('../utils/cacheStrategy');
                      if (user) cacheStrategy.invalidate('delete', { userId: getPrivateCacheScope().scope, imageId: toDeleteId, albumId });
                    } catch { }
                    try {
                      if (navigator.serviceWorker?.controller && toDeleteFile) {
                        navigator.serviceWorker.controller.postMessage({
                          type: 'EVICT_MEDIA',
                          filename: toDeleteFile,
                          base: toDeleteFile.replace(/\.[^.]+$/, '')
                        });
                      }
                    } catch { }
                    if (onRefresh) onRefresh(true);
                  } catch (error) {
                    console.error('Failed to delete image:', error);
                    alert('Failed to delete image');
                  }
                }}
                data-tooltip="Delete"
              >
                <FiTrash2 size={20} />
              </button>
            </div>
          </div>

          {viewMode === 'single' ? (
            <div className="lightbox-single">
              <div className="lightbox-image-container">
                {/* Loading spinner */}
                {!hiResReady && !previewReady && !hiResFailed && (
                  <div className="lightbox-loading">
                    <div className="spinner"></div>
                  </div>
                )}

                {(() => {
                  const displayUrl = hiResSrc;
                  const showPreview = previewSrc && previewSrc !== displayUrl;
                  const localRotation = lightboxImage?._localRotation || 0;
                  const imageStyle = {
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotate(${localRotation}deg)`,
                    transformOrigin: 'center center',
                    cursor: 'grab',
                    transition: localRotation !== 0 ? 'transform 0.2s ease-out' : undefined
                  };
                  const onHiResError = () => {
                    if (lightboxImage?.cdn_urls && !lightboxImage?._force_origin) {
                      markOriginFallback(lightboxImage.id);
                      setReadySrc(null);
                      setHiResFailed(false);
                      return;
                    }
                    if (hiResVariant === 'xl') {
                      setHiResVariant('lg');
                      setReadySrc(null);
                      setHiResFailed(false);
                      return;
                    }
                    setHiResFailed(true);
                  };
                  return (
                    <>
                      <div
                        key={lightboxImage.id}
                        className={`lightbox-image-stack ${transitionDirection < 0 ? 'slide-in-left' : 'slide-in-right'}`}
                      >
                        {showPreview && (
                          <img
                            className={`lightbox-image lightbox-preview ${hiResReady ? 'is-hidden' : ''} ${zoom > 1 ? 'is-zoomed' : ''}`}
                            src={previewSrc}
                            onLoad={() => setPreviewReady(true)}
                            alt={lightboxImage.title || 'Photo'}
                            draggable={false}
                            aria-hidden="true"
                            style={imageStyle}
                          />
                        )}
                        {displayUrl && <img
                          key={displayUrl}
                          ref={lightboxImageRef}
                          className={`lightbox-image lightbox-full ${hiResReady ? 'is-ready' : ''} ${zoom > 1 ? 'is-zoomed' : ''}`}
                          src={displayUrl}
                          decoding="async"
                          fetchPriority="high"
                          alt={lightboxImage.title || 'Photo'}
                          draggable={false}
                          style={hiResFailed ? { ...imageStyle, display: 'none' } : imageStyle}
                          onLoad={() => {
                            setReadySrc(displayUrl);
                            setHiResFailed(false);
                          }}
                          onError={onHiResError}
                          onWheel={(e) => {
                            if (e.ctrlKey || e.metaKey) {
                              e.preventDefault();
                              applyZoom(e.deltaY > 0 ? -0.15 : 0.15, { x: e.clientX, y: e.clientY });
                            }
                          }}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            if (zoom === 1) {
                              // Zoom in to 2x centered on click point
                              applyZoom(1, { x: e.clientX, y: e.clientY });
                            } else {
                              // Reset to 1x
                              setZoom(1);
                              setPan({ x: 0, y: 0 });
                            }
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            panRef.current = {
                              active: true,
                              startX: e.clientX,
                              startY: e.clientY,
                              originX: pan.x,
                              originY: pan.y
                            };
                            lightboxImageRef.current.style.cursor = 'grabbing';
                          }}
                          onMouseUp={() => {
                            panRef.current.active = false;
                            if (lightboxImageRef.current) lightboxImageRef.current.style.cursor = 'grab';
                          }}
                          onMouseLeave={() => {
                            panRef.current.active = false;
                            if (lightboxImageRef.current) lightboxImageRef.current.style.cursor = 'grab';
                          }}
                          onMouseMoveCapture={(e) => {
                            if (!panRef.current.active) return;
                            const dx = e.clientX - panRef.current.startX;
                            const dy = e.clientY - panRef.current.startY;
                            const nextX = panRef.current.originX + dx;
                            const nextY = panRef.current.originY + dy;
                            setPan({ x: nextX, y: nextY });
                          }}
                        />}
                      </div>
                    </>
                  );
                })()}

                {showImageInfo && (
                  <div
                    className="lightbox-info-panel"
                    style={{ transform: `translate(${infoPosition.x}px, ${infoPosition.y}px)` }}
                  >
                    <div className="lightbox-info-drag-handle" onMouseDown={startInfoDrag}>
                      <span className="drag-grip">
                        <span></span><span></span><span></span>
                        <span></span><span></span><span></span>
                        <span></span><span></span><span></span>
                      </span>
                      <span className="drag-title">Photo Info</span>
                    </div>
                    <div className="info-section">
                      <h4>File Information</h4>
                      <div className="info-row">
                        <span className="info-label">Filename:</span>
                        {isEditingFilename ? (
                          <div className="info-value-edit">
                            <div className="filename-edit-wrapper">
                              <input
                                type="text"
                                ref={(el) => {
                                  if (el) {
                                    // Show only base name without extension
                                    const base = lightboxImage.filename.replace(/\.[^.]+$/, '');
                                    el.value = base;
                                    el.focus();
                                    el.select();
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') {
                                    setIsEditingFilename(false);
                                  }
                                }}
                              />
                              <span className="filename-ext">{lightboxImage.filename.match(/\.[^.]+$/)?.[0] || ''}</span>
                            </div>
                            <div className="edit-actions">
                              <button
                                className="lightbox-rotate-btn"
                                onClick={async (e) => {
                                  const input = e.currentTarget.parentElement.previousElementSibling.querySelector('input');
                                  let newBase = input.value.trim();

                                  const oldFilename = lightboxImage.filename;
                                  const oldExt = oldFilename.match(/\.[^.]+$/)?.[0] || '';
                                  const oldBase = oldFilename.replace(/\.[^.]+$/, '');

                                  // Strip any extension the user might have typed, then add the correct one
                                  newBase = newBase.replace(/\.[^.]+$/, '');
                                  const newName = newBase + oldExt;

                                  if (!newBase || newName === oldFilename) {
                                    setIsEditingFilename(false);
                                    return;
                                  }

                                  try {
                                    e.currentTarget.disabled = true;
                                    e.currentTarget.innerHTML = 'Saving...';

                                    const response = await apiFetch(`/api/images/${lightboxImage.id}`, {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ filename: newName })
                                    });

                                    // Check if the request was successful
                                    if (!response.ok) {
                                      let errorMsg = 'Failed to rename file';
                                      try {
                                        const errorData = await response.json();
                                        errorMsg = errorData.error || errorData.message || errorMsg;
                                      } catch {}
                                      throw new Error(errorMsg);
                                    }

                                    // Evict old media from cache
                                    if (navigator.serviceWorker?.controller) {
                                      navigator.serviceWorker.controller.postMessage({
                                        type: 'EVICT_MEDIA',
                                        filename: oldFilename,
                                        base: oldBase
                                      });
                                    }
                                    photoCache.evictImage(oldFilename).catch(() => { });

                                    // Invalidate caches
                                    try {
                                      const { cacheStrategy } = await import('../utils/cacheStrategy');
                                      if (user) cacheStrategy.invalidate('edit', { userId: getPrivateCacheScope().scope, imageId: lightboxImage.id });
                                    } catch { }

                                    // Close lightbox and refresh to ensure clean state
                                    closeLightbox();
                                    if (onRefresh) onRefresh(true);
                                  } catch (err) {
                                    console.error('Rename error:', err);
                                    alert('Failed to rename file: ' + (err.message || 'Unknown error'));
                                    e.currentTarget.disabled = false;
                                    e.currentTarget.innerHTML = 'Save';
                                  }
                                }}
                              >
                                Save
                              </button>
                              <button
                                className="lightbox-rotate-btn"
                                onClick={() => setIsEditingFilename(false)}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="info-value">
                            {lightboxImage.filename}
                            <button
                              className="edit-icon-btn"
                              onClick={() => setIsEditingFilename(true)}
                              title="Rename file"
                            >
                              <FiEdit2 size={12} />
                            </button>
                          </span>
                        )}
                      </div>
                      {lightboxImage.title && (
                        <div className="info-row">
                          <span className="info-label">Title:</span>
                          <span className="info-value">{lightboxImage.title}</span>
                        </div>
                      )}
                      {lightboxImage.width && lightboxImage.height && (
                        <div className="info-row">
                          <span className="info-label">Dimensions:</span>
                          <span className="info-value">{lightboxImage.width} × {lightboxImage.height}</span>
                        </div>
                      )}
                      {lightboxImage.file_size && (
                        <div className="info-row">
                          <span className="info-label">File Size:</span>
                          <span className="info-value">{(lightboxImage.file_size / (1024 * 1024)).toFixed(2)} MB</span>
                        </div>
                      )}
                      {lightboxImage.created_at && (
                        <div className="info-row">
                          <span className="info-label">Created:</span>
                          <span className="info-value">{new Date(lightboxImage.created_at).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    {(() => {
                      const exif = buildUiExif(lightboxImage);
                      const hasRawExif = lightboxImage?.exif_data && (
                        typeof lightboxImage.exif_data === 'object'
                          ? Object.keys(lightboxImage.exif_data).length > 0
                          : lightboxImage.exif_data !== '{}'
                      );
                      if (!exif && !hasRawExif) return null;
                      return (
                        <div className="info-section">
                          <h4>EXIF Data</h4>
                          {exif?.camera && (
                            <div className="info-row">
                              <span className="info-label">Camera:</span>
                              <span className="info-value">{exif.camera}</span>
                            </div>
                          )}
                          {exif?.lens && (
                            <div className="info-row">
                              <span className="info-label">Lens:</span>
                              <span className="info-value">{exif.lens}</span>
                            </div>
                          )}
                          {exif?.aperture && (
                            <div className="info-row">
                              <span className="info-label">Aperture:</span>
                              <span className="info-value">f/{exif.aperture}</span>
                            </div>
                          )}
                          {exif?.shutter_speed && (
                            <div className="info-row">
                              <span className="info-label">Shutter:</span>
                              <span className="info-value">{exif.shutter_speed}s</span>
                            </div>
                          )}
                          {exif?.iso && (
                            <div className="info-row">
                              <span className="info-label">ISO:</span>
                              <span className="info-value">{exif.iso}</span>
                            </div>
                          )}
                          {exif?.focal_length && (
                            <div className="info-row">
                              <span className="info-label">Focal Length:</span>
                              <span className="info-value">{exif.focal_length}mm</span>
                            </div>
                          )}
                          {exif?.taken_at && (
                            <div className="info-row">
                              <span className="info-label">Date Taken:</span>
                              <span className="info-value">{new Date(exif.taken_at).toLocaleString()}</span>
                            </div>
                          )}
                          {hasRawExif && (
                            <details className="exif-raw-viewer">
                              <summary>View Raw EXIF</summary>
                              <pre>{JSON.stringify(
                                typeof lightboxImage.exif_data === 'string'
                                  ? JSON.parse(lightboxImage.exif_data)
                                  : lightboxImage.exif_data,
                                null, 2
                              )}</pre>
                            </details>
                          )}
                        </div>
                      );
                    })()}
                    {lightboxImage.tags && lightboxImage.tags.length > 0 && (
                      <div className="info-section">
                        <h4>Tags</h4>
                        <div className="tag-list">
                          {lightboxImage.tags.map(tag => (
                            <span key={tag.id} className="tag-item">
                              {tag.label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="lightbox-contact-sheet">
              <div className="contact-sheet-grid">
                {localImages.map((img, index) => (
                  <div
                    key={img.id}
                    className={`contact-sheet-item ${lightboxImage.id === img.id ? 'active' : ''}`}
                    onClick={() => {
                      setLightboxImage(img);
                      setViewMode('single');
                    }}
                  >
                    <span className="contact-sheet-index">{index + 1}</span>
                    <img
                      src={buildVariantUrl(img, 'xl')}
                      alt={img.title || 'Photo'}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Share to Strip Modal */}
          {stripEnabled && showShareModal && (
            <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
              <div className="share-modal" onClick={e => e.stopPropagation()}>
                <div className="share-modal-header">
                  <h3>Share to The Strip</h3>
                  <button onClick={() => setShowShareModal(false)} className="modal-close">
                    <FiX size={20} />
                  </button>
                </div>
                <div className="share-modal-content">
                  <div className="share-preview">
                    <img
                      src={buildVariantUrl(lightboxImage, 'xl')}
                      alt={lightboxImage.title || 'Photo'}
                    />
                  </div>
                  <textarea
                    value={shareCaption}
                    onChange={(e) => setShareCaption(e.target.value)}
                    placeholder="Add a caption... (optional)"
                    className="share-caption-input"
                    rows={3}
                  />
                  <div className="share-actions">
                    <button
                      className="btn-secondary"
                      onClick={() => setShowShareModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-primary"
                      onClick={async () => {
                        setSharing(true);
                        try {
                          const response = await fetch('/api/strip/share', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({
                              image_id: lightboxImage.id,
                              caption: shareCaption || null
                            })
                          });
                          if (response.ok) {
                            alert('Shared to The Strip!');
                            setShowShareModal(false);
                            setShareCaption('');
                          } else {
                            const error = await response.json();
                            alert(error.message || 'Failed to share');
                          }
                        } catch (error) {
                          alert('Failed to share: ' + error.message);
                        } finally {
                          setSharing(false);
                        }
                      }}
                      disabled={sharing}
                    >
                      {sharing ? 'Sharing...' : 'Share to The Strip'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile Photo Viewer */}
      {showMobileViewer && (
        <MobilePhotoSwiper
          images={localImages}
          initialIndex={mobileViewerIndex}
          thumbnailVariant={useFastThumbnails ? 'sm' : 'md'}
          onClose={() => setShowMobileViewer(false)}
          onLike={toggleLike}
          onDelete={deleteImage}
          onRotate={rotateImage}
          onShare={(img) => {
            if (navigator.share) {
              navigator.share({
                title: img.title || img.filename,
                text: img.caption,
                url: window.location.origin + `/media/originals/${img.filename}`
              }).catch(() => { });
            }
          }}
        />
      )}
    </>
  );
}
